import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Play, Calendar, Clock, Youtube, ExternalLink, Podcast as PodcastIcon, Share2 } from "lucide-react";
import { findEpisode, EPISODES, SPOTIFY_SHOW_ID } from "../data/episodes";

export default function PodcastEpisode() {
  const { slug } = useParams();
  const ep = findEpisode(slug);

  if (!ep) {
    return (
      <div className="py-24 text-center">
        <div className="max-w-xl mx-auto px-6">
          <div className="eyebrow mb-3">Episode not found</div>
          <h1 className="font-serif text-3xl text-brand-charcoal mb-4">That episode is not in the library yet.</h1>
          <p className="text-slate-600 mb-8">The full 36-episode catalogue lives on our YouTube channel while we finish importing show notes here.</p>
          <Link to="/podcast" className="btn-primary" data-testid="notfound-back">Back to Voices of Change</Link>
        </div>
      </div>
    );
  }

  const canonical = `https://allthingssocialimpact.com/podcast/${ep.slug}`;
  const thumb = `https://i.ytimg.com/vi/${ep.youtube_id}/maxresdefault.jpg`;

  const podcastLd = {
    "@context": "https://schema.org",
    "@type": "PodcastEpisode",
    name: ep.title,
    description: ep.long_summary || ep.excerpt,
    datePublished: ep.iso_date,
    duration: ep.duration_iso,
    url: canonical,
    thumbnailUrl: thumb,
    episodeNumber: ep.ep_num,
    partOfSeries: {
      "@type": "PodcastSeries",
      name: "Voices of Change - Stories That Matter",
      url: "https://allthingssocialimpact.com/podcast",
    },
    associatedMedia: {
      "@type": "VideoObject",
      contentUrl: ep.youtube_url,
      embedUrl: `https://www.youtube.com/embed/${ep.youtube_id}`,
      name: ep.title,
      description: ep.excerpt,
      uploadDate: ep.iso_date,
      thumbnailUrl: thumb,
      duration: ep.duration_iso,
    },
    author: [
      { "@type": "Person", name: "Andrea Kanaris" },
      { "@type": "Person", name: ep.guest },
    ],
    keywords: (ep.keywords || []).join(", "),
  };

  return (
    <div data-testid={`episode-detail-${ep.slug}`}>
      <Helmet>
        <title>{`${ep.title} - Voices of Change (${ep.number}) | All Things Social Impact`}</title>
        <meta name="description" content={ep.excerpt} />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={ep.title} />
        <meta property="og:description" content={ep.excerpt} />
        <meta property="og:image" content={thumb} />
        <meta property="og:url" content={canonical} />
        <meta property="article:published_time" content={ep.iso_date} />
        <meta property="article:author" content="Andrea Kanaris" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={ep.title} />
        <meta name="twitter:description" content={ep.excerpt} />
        <meta name="twitter:image" content={thumb} />
        <script type="application/ld+json">{JSON.stringify(podcastLd)}</script>
      </Helmet>

      {/* Hero */}
      <section className="bg-brand-blue-deep text-white py-16 lg:py-24">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
          <Link to="/podcast" data-testid="episode-back" className="inline-flex items-center gap-2 text-brand-yellow text-xs uppercase tracking-widest font-semibold hover:underline">
            <ArrowLeft className="w-3.5 h-3.5" /> All episodes
          </Link>
          <div className="mt-6 flex items-center gap-3 flex-wrap">
            <span className="text-[10px] uppercase tracking-widest font-bold text-brand-yellow">{ep.number}</span>
            <span className="text-white/40">·</span>
            <span className="text-xs text-white/80 flex items-center gap-1.5"><Calendar className="w-3 h-3" /> {ep.releaseDate}</span>
            <span className="text-white/40">·</span>
            <span className="text-xs text-white/80 flex items-center gap-1.5"><Clock className="w-3 h-3" /> {ep.duration}</span>
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.1] mt-4 max-w-3xl">{ep.title}</h1>
          <p className="text-lg text-white/80 mt-6 max-w-2xl leading-relaxed">With {ep.guest}. Hosted by Andrea Kanaris.</p>
        </div>
      </section>

      {/* Player + summary */}
      <section className="py-14 bg-white">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-10">
          <div>
            <div className="w-full aspect-video bg-black shadow-xl">
              <iframe
                data-testid="episode-player"
                className="w-full h-full"
                src={`https://www.youtube.com/embed/${ep.youtube_id}?rel=0`}
                title={ep.title}
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href={ep.youtube_url} target="_blank" rel="noreferrer" data-testid="episode-watch-yt" className="btn-primary"><Youtube className="w-4 h-4" /> Watch on YouTube</a>
              <a href={`https://open.spotify.com/show/${SPOTIFY_SHOW_ID}`} target="_blank" rel="noreferrer" data-testid="episode-listen-spotify" className="btn-secondary"><PodcastIcon className="w-4 h-4" /> Listen on Spotify</a>
            </div>

            {ep.long_summary && (
              <div className="mt-12">
                <h2 className="font-serif text-2xl text-brand-charcoal mb-4">Episode summary</h2>
                <p className="text-slate-700 leading-relaxed">{ep.long_summary}</p>
              </div>
            )}

            {ep.chapters?.length > 0 && (
              <div className="mt-12" data-testid="episode-chapters">
                <h2 className="font-serif text-2xl text-brand-charcoal mb-4">Chapters</h2>
                <ol className="border-t border-slate-200">
                  {ep.chapters.map((c) => (
                    <li key={c.t} className="border-b border-slate-200 py-3 flex items-baseline gap-4">
                      <a
                        href={`${ep.youtube_url}?t=${chapterTimeToSeconds(c.t)}s`}
                        target="_blank"
                        rel="noreferrer"
                        className="font-mono text-sm text-brand-orange font-semibold w-16 flex-shrink-0 hover:underline"
                      >{c.t}</a>
                      <span className="text-slate-700 text-sm leading-snug">{c.label}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {ep.takeaways?.length > 0 && (
              <div className="mt-12">
                <h2 className="font-serif text-2xl text-brand-charcoal mb-4">Key takeaways</h2>
                <ul className="space-y-3">
                  {ep.takeaways.map((t, i) => (
                    <li key={i} className="flex items-start gap-3 text-slate-700">
                      <span className="w-1.5 h-1.5 mt-2.5 bg-brand-orange flex-shrink-0" aria-hidden="true" />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-12 p-6 bg-slate-50 border border-slate-200">
              <div className="eyebrow mb-2">Transcript</div>
              <p className="text-slate-600 text-sm leading-relaxed">
                A full transcript is being finalised. You can watch with auto-generated captions on
                {" "}<a href={ep.youtube_url} target="_blank" rel="noreferrer" className="text-brand-orange font-semibold hover:underline">YouTube</a>{" "}
                or listen with a device screen reader via
                {" "}<a href={`https://open.spotify.com/show/${SPOTIFY_SHOW_ID}`} target="_blank" rel="noreferrer" className="text-brand-orange font-semibold hover:underline">Spotify</a>.
              </p>
            </div>
          </div>

          {/* Guest sidebar */}
          <aside className="lg:sticky lg:top-24 lg:self-start">
            <div className="bg-slate-50 border border-slate-200 p-6">
              <div className="eyebrow mb-3">Guest</div>
              <h3 className="font-serif text-xl text-brand-charcoal mb-3">{ep.guest}</h3>
              {ep.guest_bio && <p className="text-slate-700 text-sm leading-relaxed mb-4">{ep.guest_bio}</p>}
              {ep.guest_links?.length > 0 && (
                <ul className="space-y-2" data-testid="guest-links">
                  {ep.guest_links.map((l) => (
                    <li key={l.url}>
                      <a href={l.url} target="_blank" rel="noreferrer" className="text-sm text-brand-orange font-semibold hover:underline inline-flex items-center gap-1.5">
                        {l.label} <ExternalLink className="w-3 h-3" />
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {ep.keywords?.length > 0 && (
              <div className="mt-6">
                <div className="eyebrow mb-3">Topics</div>
                <div className="flex flex-wrap gap-2">
                  {ep.keywords.map((k) => (
                    <span key={k} className="text-[10px] uppercase tracking-widest font-semibold bg-white border border-slate-200 text-slate-600 px-2 py-1">{k}</span>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-6">
              <button data-testid="episode-share" onClick={() => share(ep, canonical)} className="text-xs uppercase tracking-widest font-semibold text-brand-charcoal hover:text-brand-orange inline-flex items-center gap-1.5">
                <Share2 className="w-3.5 h-3.5" /> Share this episode
              </button>
            </div>
          </aside>
        </div>
      </section>

      {/* Next up */}
      <NextEpisodes currentSlug={ep.slug} />
    </div>
  );
}

function NextEpisodes({ currentSlug }) {
  const others = EPISODES.filter((e) => e.slug !== currentSlug).slice(0, 3);
  if (others.length === 0) return null;
  return (
    <section className="py-16 bg-slate-50 border-t border-slate-200">
      <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
        <div className="eyebrow mb-3">Keep listening</div>
        <h2 className="font-serif text-3xl text-brand-charcoal mb-8">More Voices of Change.</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {others.map((e) => (
            <Link key={e.slug} to={`/podcast/${e.slug}`} data-testid={`related-${e.slug}`} className="group bg-white border border-slate-200 p-6 hover:border-brand-orange transition-colors">
              <div className="text-[10px] uppercase tracking-widest font-bold text-brand-orange mb-3">{e.number}</div>
              <h3 className="font-serif text-lg text-brand-charcoal leading-snug mb-2 group-hover:text-brand-orange transition-colors">{e.title}</h3>
              <div className="text-xs text-slate-500 mb-3">With {e.guest}</div>
              <div className="text-xs text-brand-orange font-semibold inline-flex items-center gap-1.5">
                Read the show notes <Play className="w-3 h-3" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function chapterTimeToSeconds(t) {
  const parts = t.split(":").map((n) => parseInt(n, 10));
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return 0;
}

function share(ep, url) {
  if (navigator.share) {
    navigator.share({ title: ep.title, text: ep.excerpt, url }).catch(() => {});
  } else if (navigator.clipboard) {
    navigator.clipboard.writeText(url);
  }
}
